<template>
  <div
    ref="playerContainer"
    :class="['video-player-container', { compact }]"
    data-external="video-player-container-element"
  >
    <media-player
      ref="player"
      :class="{ 'no-loading-indicator': isExternalVideo, custom: !!brandColor }"
      :src="source"
      :poster="thumbUrl || undefined"
      playsinline
      :muted="muted"
      :autoplay="autoplay"
      :data-external-video="isExternalVideo"
      :duration="mediaDuration"
      :controls-delay="3000"
      :data-display-controls="controls"
      :can-google-cast="hasChromecastSupport"
      :data-has-google-cast-support="hasChromecastSupport"
      stream-type="on-demand"
      cross-origin="anonymous"
      @provider-change="handleProviderChange"
      @destroy="() => (player = null)"
      @error="handleError"
      @can-play="addEventListeners"
      @google-cast-prompt-error="handleChromecastError"
      @click="$emit('video-clicked')"
      @time-update="throttledHandleTimeUpdate"
    >
      <media-poster v-if="thumbUrl" :src="thumbUrl" class="vds-poster"></media-poster>
      <media-provider> </media-provider>
      <media-video-layout color-scheme="light" no-audio-gain> </media-video-layout>
    </media-player>
  </div>
</template>

<script lang="ts" setup>
import { logger } from '@getaccept/lib-shared/src/logger';
import { ref, computed, watch, onMounted, onBeforeUnmount, useTemplateRef } from 'vue';
import 'vidstack/bundle';
import type {
  VideoSrc,
  MediaProviderChangeEvent,
  MediaErrorEvent,
  GoogleCastPromptErrorEvent,
} from 'vidstack';
import { useThrottleFn, useElementVisibility } from '@vueuse/core';
import type { MediaPlayerElement } from 'vidstack/elements';
import { isYouTubeProvider } from 'vidstack';
import type { UiStyling } from '@getaccept/lib-shared/src/branding/types/ui-styling';
import * as VideoLinkHelper from '../../link/helpers/video-link.helper';
import * as VideoRecorderHelper from '../../recorder/helpers/video-recorder.helper';

const {
  blob = null,
  url = null,
  controls = true,
  autoplay = false,
  muted = false,
  playOnHover = false,
  ratio = '16 / 9',
  thumbUrl = null,
  compact = false,
  videoId = null,
  uiStyling = null,
  pauseWhenNotVisible = false,
} = defineProps<{
  blob?: Blob | null;
  url?: string | null;
  controls?: boolean;
  autoplay?: boolean;
  muted?: boolean;
  playOnHover?: boolean;
  ratio?: string;
  thumbUrl?: string | null;
  compact?: boolean;
  videoId?: string | null;
  uiStyling?: UiStyling | null;
  pauseWhenNotVisible?: boolean;
}>();

const emit = defineEmits<{
  timeupdate: [number];
  playing: [];
  pause: [];
  ended: [];
  error: [];
  'video-clicked': [];
}>();

const player = ref<MediaPlayerElement | null>(null);
const playerContainer = useTemplateRef<HTMLDivElement>('playerContainer');
const mediaDuration = ref(0);

const isBlobUrl = computed(() => url && url.startsWith('blob:'));

const setPoster = (value: string) => {
  if (!player.value) {
    return;
  }
  player.value.poster = value;
};

watch(
  () => thumbUrl,
  () => setPoster(thumbUrl || '')
);

const isExternalVideo = computed(
  () => VideoLinkHelper.isVimeoUrl(url || '') || VideoLinkHelper.isYoutubeUrl(url || '')
);

const source = computed<string | VideoSrc>(() => {
  if (!url) return '';

  if (isBlobUrl.value) {
    return {
      src: url,
      type: 'video/webm',
    };
  }

  if (isExternalVideo.value) {
    return url;
  }

  return {
    src: url,
    // TODO: Investigate why AVI doesn't work with this type but MOV does
    type: 'video/mp4',
  };
});

const handleTimeupdate = () => {
  if (!player.value) return;
  const time = Math.floor(player.value.currentTime * 1000);
  emit('timeupdate', time);
};

const handleError = (e: MediaErrorEvent) => {
  const error = e.detail;

  // Handle specific error codes
  switch (error.code) {
    case 1: // MEDIA_ERR_ABORTED
      console.warn('Media playback aborted by user');
      break;
    case 2: // MEDIA_ERR_NETWORK
      logger.error('Network error while loading video');
      break;
    case 3: // MEDIA_ERR_DECODE
      logger.error('Error decoding video');
      break;
    case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
      console.warn('Video format not supported or still processing');
      break;
    default:
      if (error.message?.includes('provider destroyed')) {
        // Handle provider destroyed error
        console.warn('Provider was destroyed, possibly due to component unmounting');
        return;
      }
      if (error.message?.includes('AbortError')) {
        console.warn('Request was aborted, possibly due to navigation or unmounting');
        return;
      }
      logger.error('Video player error:', error);
  }

  emit('error');
};

const hasChromecastSupport = computed(() => {
  const { userAgent } = navigator;
  const isChrome = userAgent.includes('Chrome');
  if (!isChrome) {
    return false;
  }
  const chromeVersion = userAgent.split('Chrome/')[1].split(' ')[0];
  return parseInt(chromeVersion, 10) >= 129;
});

const throttledHandleTimeUpdate = useThrottleFn(handleTimeupdate, 1000);

const handlePlaying = () => {
  if (player.value) {
    player.value.play();
    emit('playing');
  }
};

const handlePause = () => {
  if (player.value) {
    player.value.pause();
  }
  emit('pause');
};

const handleEnded = () => {
  emit('ended');
};

const handleMouseEnter = () => {
  if (player.value) {
    player.value.play();
  }
};

const handleMouseLeave = () => {
  if (player.value) {
    player.value.pause();
  }
};

const addEventListeners = () => {
  if (!player.value) return;

  player.value.addEventListener('playing', handlePlaying);
  player.value.addEventListener('pause', handlePause);
  player.value.addEventListener('ended', handleEnded);

  if (playOnHover) {
    player.value.addEventListener('mouseenter', handleMouseEnter);
    player.value.addEventListener('mouseleave', handleMouseLeave);
  }
};

const removeEventListeners = () => {
  player.value?.removeEventListener('playing', handlePlaying);
  player.value?.removeEventListener('pause', handlePause);
  player.value?.removeEventListener('ended', handleEnded);

  if (playOnHover) {
    player.value?.removeEventListener('mouseenter', handleMouseEnter);
    player.value?.removeEventListener('mouseleave', handleMouseLeave);
  }
};

const handleProviderChange = (e: MediaProviderChangeEvent) => {
  const provider = e.detail;

  if (!provider) {
    return;
  }

  try {
    if (isYouTubeProvider(provider)) {
      provider.referrerPolicy = 'no-referrer-when-downgrade';
      provider.iframe.referrerPolicy = 'no-referrer-when-downgrade';
    }
  } catch (error) {
    logger.error('Error configuring provider:', error);
    emit('error');
  }
};

const handleChromecastError = (event: GoogleCastPromptErrorEvent) => {
  const error = event.detail;
  logger.error('Chromecast cast error: ', error);
};

const brandColor = computed(() => uiStyling?.color);

const playerIsVisible = useElementVisibility(playerContainer, {
  threshold: 0.5,
});

watch(playerIsVisible, isVisible => {
  if (!isVisible && pauseWhenNotVisible) {
    player.value?.pause();
  }
});

onMounted(async () => {
  try {
    if (ratio && player.value) {
      player.value.style.aspectRatio = ratio;
    }

    const currentDuration = player.value?.state?.duration;

    if (isBlobUrl.value && blob) {
      mediaDuration.value = await VideoRecorderHelper.calculateMediaDuration(blob);
    } else if (!currentDuration && !isExternalVideo.value) {
      setTimeout(() => {
        mediaDuration.value = VideoRecorderHelper.getVideoDurationFromSessionStorage(videoId || '');
      }, 300);
    }
  } catch (error) {
    logger.error('Error initializing video player:', error);
  }
});

onBeforeUnmount(() => {
  if (player.value) {
    try {
      removeEventListeners();
      player.value.destroy();
    } catch (error) {
      console.warn('Error destroying player:', error);
    }
  }
});
</script>

<style lang="scss" scoped>
.video-player-container {
  width: 100%;
  height: 100%;
  overflow: hidden;

  &.compact {
    width: 80%;
  }

  @media only screen and (max-width: $xs) {
    border-radius: 0;
  }

  video {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
}
</style>

<style lang="scss">
.vds-video-layout {
  font-size: 16px;

  --video-font-family: var(--default-font-family);
  --video-brand: var(--brand-orange);
}

.custom {
  .vds-video-layout {
    --video-brand: v-bind(brandColor);
  }
}

media-video-layout {
  .vds-buffering-indicator {
    transform: translate(0, 0);

    .vds-buffering-spinner {
      line-height: 0;
    }
  }
}

[data-external-video='true'] {
  media-video-layout {
    .vds-loading-spinner,
    .vds-buffering-indicator {
      display: none;
    }
  }
}

[data-display-controls='false'] {
  media-controls {
    pointer-events: unset !important;
  }

  .vds-controls-group {
    visibility: hidden;
  }

  .vds-start-duration {
    visibility: hidden;
  }
}

[data-has-google-cast-support='false'] {
  .vds-google-cast-button {
    display: none;
  }
}

[data-media-player][data-layout='video']:not([data-fullscreen]) {
  border: none;
}

@media screen and (max-width: $xs) {
  .vds-controls-group {
    height: 30%;
  }
}

media-player {
  z-index: 0;
  border: none;
}

media-menu-items {
  z-index: $z-modal + 3;
}

video::-internal-media-controls-overlay-cast-button {
  display: none;
}

[data-media-player] {
  contain: layout;
}

iframe.vds-youtube[data-no-controls] {
  height: 100%;
}
</style>
